import { FormattedMessage, useIntl } from '@futrli/localisation';
import { useWindowSize } from '@futrli/shared-services';
import { UISystemIcon } from '@futrli/ui-components';
import classNames from 'classnames';
import React, { FocusEvent, ReactNode, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Typography from 'carbon-react/lib/components/typography';
import Textbox from 'carbon-react/lib/components/textbox';
import Button from 'carbon-react/lib/components/button';
import Link from 'carbon-react/lib/components/link';

import styles from '../../core/common.scss';
import { DESKTOP_LAYOUT_LIMIT, FORGOT_PASSWORD_SUBMIT_FORM_ID, FORM_FIELDS, LOGIN_ROUTE } from '../../core/constants';
import { AuthError } from '../../core/types';
import { getFormValidator } from '../../utils/validation-utils';

interface Props {
  forgotPasswordError: AuthError;
  isLoading: boolean;
  onSubmit: (username: string) => void;
}

type ForgotPasswordErrorData = Record<string, ReactNode>;

export const ForgotPasswordForm = ({ forgotPasswordError, isLoading, onSubmit }: Props) => {
  const [username, setUsername] = useState('');
  const [usernameTouched, setUsernameTouched] = useState(false);

  const navigate = useNavigate();

  const { width: windowWidth } = useWindowSize();

  useEffect(() => {
    if (typeof windowWidth === 'number' && windowWidth >= DESKTOP_LAYOUT_LIMIT) {
      document.body.classList.add(styles.bodyStyles);
    }

    return () => {
      if (typeof windowWidth === 'number' && windowWidth >= DESKTOP_LAYOUT_LIMIT) {
        document.body.classList.remove(styles.bodyStyles);
      }
    };
  }, [windowWidth]);

  const intl = useIntl();

  const validate = getFormValidator([FORM_FIELDS.username], FORGOT_PASSWORD_SUBMIT_FORM_ID, intl);

  const [errors, setErrors] = useState<ForgotPasswordErrorData>(
    validate(({ username } as unknown) as Record<string, string>)
  );

  const handleUsernameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (!usernameTouched) {
        setUsernameTouched(true);
      }
      setErrors(
        validate({
          username: newValue
        })
      );
      setUsername(newValue);
    },
    [usernameTouched, validate]
  );

  const handleBlur = useCallback(
    (_e: FocusEvent<HTMLInputElement>) => {
      if (!usernameTouched) {
        setUsernameTouched(true);
      }
    },
    [usernameTouched]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const disabled = isLoading || Object.keys(errors).length > 0;
    if (e.key === 'Enter' && !disabled) {
      onSubmit(username);
    }
  };

  const handleLoginClick = useCallback(() => navigate(LOGIN_ROUTE), [navigate]);

  const handleSubmit = (_e: React.MouseEvent) => onSubmit(username);

  const dataError = Object.keys(errors).length > 0;

  return (
    <main className={styles.newPageContainer}>
      <div className={classNames(styles.newPageContent, styles.mobileHeight)}>
        <div className={styles.registerFormContainer}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
            <UISystemIcon iconName="dark-by-sage-logo" width={202} height={90} />
          </div>
          <Typography variant="h1" textAlign="center" marginBottom={16}>
            <FormattedMessage id="forgot_password.forgot_your_password" defaultMessage="Forgot your password?" />
          </Typography>
          <Typography fontSize="16px" textAlign="center" marginBottom={40}>
            <FormattedMessage
              id="forgot_password.reset_password_link"
              defaultMessage="We'll send you a link in an email that you can use to reset your password if we can find your account."
            />
          </Typography>
          <Textbox
            label={intl.formatMessage({ id: 'common.email_address', defaultMessage: 'Email address' })}
            placeholder={intl.formatMessage({
              id: 'common.enter_email_address',
              defaultMessage: 'Enter your email address'
            })}
            value={username}
            onChange={handleUsernameChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            name={FORM_FIELDS.username}
            error={!!errors.username && usernameTouched ? (errors.username as string) : ''}
            mb={40}
            size="large"
            disabled={isLoading}
          />
          {forgotPasswordError && (
            // TODO i18n: make sure forgotPasswordError.message supports
            <Typography fontSize="14px" color="errorRed">
              {forgotPasswordError.message}
            </Typography>
          )}
          <Button
            fullWidth
            disabled={isLoading || dataError}
            mb={40}
            onClick={handleSubmit}
            buttonType="primary"
            size="large"
          >
            <FormattedMessage id="common.continue" defaultMessage="Continue" />
          </Button>
          <div style={{ display: 'flex', marginBottom: 16 }}>
            <svg width="100%" height="1" viewBox="0 0 400 1" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 0.5H400" stroke="#7F7F7F" strokeWidth="0.5" />
            </svg>
          </div>
          <Link onClick={handleLoginClick} className={styles.sageCarbonAuthLink}>
            <FormattedMessage id="forgot_password.back_to_log_in" defaultMessage="Back to Log in?" />
          </Link>
        </div>
      </div>
    </main>
  );
};
